'use client'

import { useState, useEffect, use } from 'react';

interface Props {
  params: Promise<{ tenantSlug: string }>;
}

interface TenantData {
  name: string;
  config: any | null;
}

export default function TenantSitePage({ params }: Props) {
  const { tenantSlug } = use(params);
  const [data, setData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`/api/tenant-site?slug=${encodeURIComponent(tenantSlug)}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [tenantSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-400 text-lg">加载中...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-700 mb-2">页面未找到</h1>
          <p className="text-gray-500">该机构官网不存在或已下线</p>
        </div>
      </div>
    );
  }

  const config = data.config || {};
  const stats = config.stats ? JSON.parse(config.stats) : [];
  const advantages = config.advantagesJson ? JSON.parse(config.advantagesJson) : [];
  const about = config.aboutJson ? JSON.parse(config.aboutJson) : {};
  const contact = config.contactJson ? JSON.parse(config.contactJson) : {};

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">{data.name}</h1>
          <nav className="hidden md:flex gap-6 text-sm text-gray-600">
            <a href="#about" className="hover:text-gray-900">关于我们</a>
            <a href="#courses" className="hover:text-gray-900">课程体系</a>
            <a href="#contact" className="hover:text-gray-900">联系我们</a>
          </nav>
        </div>
      </header>
      
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm mb-4">
            {config.heroBadge || `欢迎来到${data.name}`}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {config.heroHeadline1 || '让每个孩子'}
          </h2>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {config.heroHeadline2 || '绽放光芒'}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {config.heroDescription || '专注个性化教育，帮助每个孩子发现自己的潜能'}
          </p>
          {config.heroCta && (
            <a href={config.heroCtaLink || '#contact'} className="inline-block mt-8 px-8 py-3 bg-amber-500 text-white rounded-full font-semibold hover:bg-amber-600 transition-colors shadow-lg">
              {config.heroCta}
            </a>
          )}
        </div>
      </section>

      {stats.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((s: any, i: number) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-amber-500">{s.value}</div>
                  <div className="text-sm text-gray-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {about.title && (
        <section id="about" className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">{about.title}</h2>
            {about.image && (
              <div className="mb-8 rounded-2xl overflow-hidden shadow-lg">
                <img src={about.image} alt={about.title} className="w-full h-64 object-cover" />
              </div>
            )}
            <p className="text-gray-600 leading-relaxed text-lg">{about.description}</p>
          </div>
        </section>
      )}

      {advantages.length > 0 && (
        <section id="courses" className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">课程体系</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {advantages.map((a: any, i: number) => (
                <div key={i} className="p-6 rounded-2xl bg-amber-50 border border-amber-100 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xl font-bold mb-4">
                    {a.icon || (i + 1)}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{a.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{a.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="contact" className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-8">联系我们</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {contact.address && (
              <div className="p-4">
                <div className="text-amber-400 text-lg mb-2">📍 地址</div>
                <p className="text-gray-400">{contact.address}</p>
              </div>
            )}
            {contact.phone && (
              <div className="p-4">
                <div className="text-amber-400 text-lg mb-2">📞 电话</div>
                <p className="text-gray-400"><a href={`tel:${contact.phone}`} className="hover:text-white transition-colors">{contact.phone}</a></p>
              </div>
            )}
            {contact.wechat && (
              <div className="p-4">
                <div className="text-amber-400 text-lg mb-2">💬 微信</div>
                <p className="text-gray-400">{contact.wechat}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-gray-950 text-gray-500 py-8">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm">
          <p>&copy; 2026 {data.name}. All rights reserved.</p>
          <p className="mt-1">技术支持：智能教务助理 SaaS 平台</p>
        </div>
      </footer>
    </div>
  );
}
